import PageContainer from "../PageContainer";
import { useData } from "../../../contexts/DataContext/DataContextt";
import {
  editClass,
  fetchClasses,
  fetchOcctooClasses,
} from "../../../services/classService";
import { useMemo, useState } from "react";
import { usePreload } from "../../../contexts/DataContext/PreloadContext";
import { ClassForm } from "./ClassForm";
import { useNavigate, useParams } from "react-router-dom";
import { usePagedQuery } from "../../../contexts/hooks/usePagedQuery";
import { classesPageCache } from "../../../pages/cache/PageCache";
import { Stack, Tooltip, IconButton } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Navigate } from "react-router-dom";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { keepOnlyCurrentLanguage } from "../../../helpers/EntityHelper";
import { localizeField } from "../../../config/localization";

const EditClass = () => {
  const { loading, language } = useData();
  const { showNotification } = usePreload();
  const [updating, setUpdating] = useState();
  const { id: classCode } = useParams();
  const navigate = useNavigate();

  if (!classCode) {
    return <Navigate to="/notfound" replace />;
  }

  const {
    mutate,
    reload,
    data: rows,
    fetched,
  } = usePagedQuery({
    fetchFunction: fetchClasses,
    searchOption: [{ id: "classCode", display: "Code" }],
    cache: classesPageCache,
    language: "all",
    searchKey: classCode,
    deps: [language],
  });

  if (fetched && !classCode) {
    return <Navigate to="/notfound" replace />;
  }

  const row = useMemo(() => {
    return {
      ...rows[0],
      className: localizeField(rows, "className"),
      classDisplayName: localizeField(rows, "classDisplayName"),
      classDescription: localizeField(rows, "classDescription"),
    };
  }, [rows]);

  const onUpdate = async (updatedRow) => {
    try {
      setUpdating(true);
      let updatedClass = updatedRow;
      //keepOnlyCurrentLanguage(updatedRow);

      await editClass(updatedClass);
      mutate(
        {
          ...updatedClass,
          id: updatedClass.classCode,
          className: updatedClass.className[language] ?? "",
          classDisplayName: updatedClass.classDisplayName[language] ?? "",
          classDescription: updatedClass.classDescription[language] ?? "",
        },
        "classCode",
      );

      showNotification(`${updatedRow?.classCode} was updated!`, "success");
    } catch (e) {
      console.log(e);
      showNotification(
        `something went wrong updating the ${updatedRow?.classCode} class.`,
        "error",
      );
    } finally {
      setUpdating(false);
    }
  };

  return (
    <PageContainer
      title="Edit Class"
      breadcrumbs={[
        { title: "ETIM Class", path: "/classNew" },
        { title: "Edit" },
      ]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Refresh feature" placement="bottom" enterDelay={300}>
            {
              <div>
                <IconButton
                  size="small"
                  aria-label="refresh"
                  onClick={async () => {
                    const updated = await reload(true);
                    if (updated) {
                      mutate(updated, "classCode");
                    }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </div>
            }
          </Tooltip>
          <Tooltip title="Back" placement="bottom" enterDelay={300}>
            {
              <div>
                <IconButton
                  size="small"
                  aria-label="refresh"
                  onClick={() => {
                    navigate(`/classes/${classCode}/includedfeatures`, {
                      state: { row },
                    });
                  }}
                >
                  <KeyboardBackspaceIcon />
                </IconButton>
              </div>
            }
          </Tooltip>
        </Stack>
      }
    >
      <ClassForm
        onSave={onUpdate}
        props={{
          row: row,
          loading: loading,
          uploading: updating,
          showCancel: false,
          saveText: "Save Changes",
          isUpdate: true,
        }}
      />
    </PageContainer>
  );
};

export default EditClass;
